import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { RefereeDto, hasAnyContactInfo } from '../../services/refereeService';
import refereeService from '../../services/refereeService';

interface RefereeListProps {
  // For public mode
  fieldId?: string;
  isPublicMode?: boolean;

  // For field management mode
  referees?: RefereeDto[];
  onUpdateStatus?: (refereeId: string, status: string) => Promise<void>;
  onRemoveReferee?: (refereeId: string) => Promise<void>;
  onSetMainReferee?: (refereeId: string, isMain: boolean) => Promise<void>;
  loading?: boolean;

  // For main referee restricted mode
  // When true: only Block/Activate/Deactivate actions are available
  // Remove and Set Main Referee actions are hidden
  restrictedMode?: boolean;

  // Current referee ID - to hide action buttons for self
  currentRefereeId?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RefereeCard = styled.div<{ $isClickable?: boolean; $isMain?: boolean }>`
  background: ${props => props.$isMain ? '#0a1f1a' : '#0d1a26'};
  border: 1px solid ${props => props.$isMain ? 'rgba(76, 175, 80, 0.4)' : 'rgba(0, 212, 255, 0.2)'};
  border-radius: 12px;
  padding: 20px;
  position: relative;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$isMain ? '#0f2a22' : '#112233'};
    border-color: ${props => props.$isMain ? 'rgba(76, 175, 80, 0.6)' : 'rgba(0, 212, 255, 0.4)'};
    transform: ${props => props.$isClickable ? 'translateY(-2px)' : 'none'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const RefereeMainInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const RefereeName = styled.h3`
  color: #00d4ff;
  font-family: 'Courier New', monospace;
  font-size: 1.1rem;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const MainBadge = styled.span`
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  color: #fff;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: bold;
  letter-spacing: 0.5px;
`;

const RefereeContact = styled.div`
  color: #8bb8d8;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BadgesRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span<{ $status: 'pending' | 'active' | 'blocked' }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Courier New', monospace;

  ${props => {
    switch (props.$status) {
      case 'active':
        return `
          background: rgba(76, 175, 80, 0.15);
          color: #4caf50;
          border: 1px solid rgba(76, 175, 80, 0.4);
        `;
      case 'pending':
        return `
          background: rgba(255, 179, 71, 0.15);
          color: #ffb347;
          border: 1px solid rgba(255, 179, 71, 0.4);
        `;
      case 'blocked':
        return `
          background: rgba(244, 67, 54, 0.15);
          color: #f44336;
          border: 1px solid rgba(244, 67, 54, 0.4);
        `;
      default:
        return `
          background: rgba(158, 158, 158, 0.15);
          color: #9e9e9e;
          border: 1px solid rgba(158, 158, 158, 0.4);
        `;
    }
  }}
`;

const VerifiedBadge = styled.span<{ $verified: boolean }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
  font-family: 'Courier New', monospace;

  ${props => props.$verified ? `
    background: rgba(0, 212, 255, 0.15);
    color: #00d4ff;
    border: 1px solid rgba(0, 212, 255, 0.4);
  ` : `
    background: rgba(158, 158, 158, 0.1);
    color: #666;
    border: 1px solid rgba(158, 158, 158, 0.3);
  `}
`;

const StatsRow = styled.div`
  display: flex;
  gap: 20px;
  margin: 16px 0;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatLabel = styled.span`
  color: #666;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Courier New', monospace;
`;

const StatValue = styled.span`
  color: #fff;
  font-size: 1rem;
  font-weight: bold;
  font-family: 'Courier New', monospace;
`;

const GameTypesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
`;

const GameTypeBadge = styled.span`
  background: rgba(0, 212, 255, 0.1);
  color: #00d4ff;
  border: 1px solid rgba(0, 212, 255, 0.3);
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.7rem;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;
`;

const ContactInfoSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background: rgba(0, 212, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(0, 212, 255, 0.1);
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #8bb8d8;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #00d4ff;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const ContactText = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #8bb8d8;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 212, 255, 0.1);
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'success' | 'warning' | 'danger' | 'default' }>`
  background: ${props => {
    switch (props.$variant) {
      case 'primary': return 'rgba(0, 212, 255, 0.15)';
      case 'success': return 'rgba(76, 175, 80, 0.15)';
      case 'warning': return 'rgba(255, 160, 0, 0.15)';
      case 'danger': return 'rgba(244, 67, 54, 0.15)';
      default: return 'rgba(255, 255, 255, 0.05)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'primary': return 'rgba(0, 212, 255, 0.4)';
      case 'success': return 'rgba(76, 175, 80, 0.4)';
      case 'warning': return 'rgba(255, 160, 0, 0.4)';
      case 'danger': return 'rgba(244, 67, 54, 0.4)';
      default: return 'rgba(255, 255, 255, 0.15)';
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'primary': return '#00d4ff';
      case 'success': return '#4caf50';
      case 'warning': return '#ffa000';
      case 'danger': return '#f44336';
      default: return '#999';
    }
  }};
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 14px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => {
      switch (props.$variant) {
        case 'primary': return 'rgba(0, 212, 255, 0.25)';
        case 'success': return 'rgba(76, 175, 80, 0.25)';
        case 'warning': return 'rgba(255, 160, 0, 0.25)';
        case 'danger': return 'rgba(244, 67, 54, 0.25)';
        default: return 'rgba(255, 255, 255, 0.1)';
      }
    }};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #8bb8d8;
  font-family: 'Courier New', monospace;
  font-style: italic;
  background: #0d1a26;
  border: 1px dashed rgba(0, 212, 255, 0.2);
  border-radius: 12px;
`;

// Confirmation Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #0d1a26;
  border: 1px solid rgba(244, 67, 54, 0.4);
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
`;

const ModalTitle = styled.h3`
  color: #f44336;
  font-family: 'Courier New', monospace;
  font-size: 1.1rem;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ModalText = styled.p`
  color: #b0b0b0;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  margin: 0 0 8px 0;
  line-height: 1.5;
`;

const ModalRefereeName = styled.span`
  color: #00d4ff;
  font-weight: bold;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ $variant: 'cancel' | 'danger' }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$variant === 'cancel' ? `
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #999;

    &:hover {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.3);
      color: #fff;
    }
  ` : `
    background: rgba(244, 67, 54, 0.2);
    border: 1px solid #f44336;
    color: #f44336;

    &:hover {
      background: #f44336;
      color: #fff;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;

  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 3px solid rgba(0, 212, 255, 0.2);
    border-radius: 50%;
    border-top-color: #00d4ff;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const RefereeList: React.FC<RefereeListProps> = ({
  fieldId,
  isPublicMode = false,
  referees: propReferees,
  onUpdateStatus,
  onRemoveReferee,
  onSetMainReferee,
  loading: propLoading = false,
  restrictedMode = false,
  currentRefereeId
}) => {
  const navigate = useNavigate();
  const [referees, setReferees] = useState<RefereeDto[]>(propReferees || []);
  const [loading, setLoading] = useState(propLoading);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  const [removeConfirm, setRemoveConfirm] = useState<{ isOpen: boolean; referee: RefereeDto | null }>({
    isOpen: false,
    referee: null
  });

  // Load referees in public mode
  useEffect(() => {
    if (isPublicMode && fieldId) {
      loadReferees();
    } else if (propReferees) {
      setReferees(propReferees);
      setLoading(propLoading);
    }
  }, [isPublicMode, fieldId, propReferees, propLoading]);

  const loadReferees = async () => {
    if (!fieldId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await refereeService.getPublicFieldReferees(fieldId);
      setReferees(data);
    } catch (err) {
      setError('Failed to load referees');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (refereeId: string, status: string) => {
    setActionLoading(prev => ({ ...prev, [refereeId]: true }));
    try {
      await onUpdateStatus?.(refereeId, status);
    } finally {
      setActionLoading(prev => ({ ...prev, [refereeId]: false }));
    }
  };

  const openRemoveConfirm = (referee: RefereeDto) => {
    setRemoveConfirm({ isOpen: true, referee });
  };

  const closeRemoveConfirm = () => {
    setRemoveConfirm({ isOpen: false, referee: null });
  };

  const handleRemoveReferee = async () => {
    if (!removeConfirm.referee) return;

    const refereeId = removeConfirm.referee.id;
    setActionLoading(prev => ({ ...prev, [refereeId]: true }));
    try {
      await onRemoveReferee?.(refereeId);
      closeRemoveConfirm();
    } finally {
      setActionLoading(prev => ({ ...prev, [refereeId]: false }));
    }
  };

  const handleSetMainReferee = async (refereeId: string, isMain: boolean) => {
    setActionLoading(prev => ({ ...prev, [`main_${refereeId}`]: true }));
    try {
      await onSetMainReferee?.(refereeId, isMain);
    } finally {
      setActionLoading(prev => ({ ...prev, [`main_${refereeId}`]: false }));
    }
  };

  const handleRefereeClick = (refereeId: string) => {
    navigate(`/referee/public/${refereeId}`);
  };

  const getFieldStatus = (referee: RefereeDto): 'pending' | 'active' | 'blocked' => {
    const statuses = Object.values(referee.fieldAssignments);
    if (statuses.includes('active')) return 'active';
    if (statuses.includes('blocked')) return 'blocked';
    return 'pending';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && isPublicMode) {
    return (
      <EmptyState>
        Error loading referees: {error}
      </EmptyState>
    );
  }

  if (referees.length === 0) {
    return (
      <EmptyState>
        {isPublicMode
          ? 'No referees assigned to this field yet.'
          : 'No referees invited yet. Start by inviting your first referee!'}
      </EmptyState>
    );
  }

  // Sort referees: main referee first, then active, then pending, then blocked
  const sortedReferees = [...referees].sort((a, b) => {
    // Main referee first
    if (a.isMainReferee && !b.isMainReferee) return -1;
    if (!a.isMainReferee && b.isMainReferee) return 1;

    // Then by status
    const statusOrder = { active: 0, pending: 1, blocked: 2 };
    const statusA = getFieldStatus(a);
    const statusB = getFieldStatus(b);
    return statusOrder[statusA] - statusOrder[statusB];
  });

  return (
    <Container>
      {sortedReferees.map(referee => {
        const fieldStatus = getFieldStatus(referee);
        const isLoading = actionLoading[referee.id];
        const isMainLoading = actionLoading[`main_${referee.id}`];

        return (
          <RefereeCard
            key={referee.id}
            $isClickable={true}
            $isMain={referee.isMainReferee}
            onClick={() => handleRefereeClick(referee.id)}
          >
            <CardHeader>
              <RefereeMainInfo>
                <RefereeName>
                  {referee.firstName} {referee.lastName}
                  {referee.isMainReferee && <MainBadge>MAIN REFEREE</MainBadge>}
                </RefereeName>
                {!isPublicMode && (
                  <RefereeContact>
                    <span>{referee.email}</span>
                    {referee.phone && <span>{referee.phone}</span>}
                  </RefereeContact>
                )}
              </RefereeMainInfo>
              <BadgesRow>
                <StatusBadge $status={fieldStatus}>
                  {fieldStatus}
                </StatusBadge>
                <VerifiedBadge $verified={referee.isEmailVerified}>
                  {referee.isEmailVerified ? 'VERIFIED' : 'NOT VERIFIED'}
                </VerifiedBadge>
              </BadgesRow>
            </CardHeader>

            <GameTypesList>
              {referee.gameTypes.map(gameType => (
                <GameTypeBadge key={gameType}>
                  {gameType}
                </GameTypeBadge>
              ))}
            </GameTypesList>

            {/* Public contact info - only shown in public mode */}
            {isPublicMode && hasAnyContactInfo(referee.contactInfo) && (
              <ContactInfoSection onClick={(e) => e.stopPropagation()}>
                {referee.contactInfo?.workEmail && (
                  <ContactItem href={`mailto:${referee.contactInfo.workEmail}`}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                    {referee.contactInfo.workEmail}
                  </ContactItem>
                )}
                {referee.contactInfo?.phone && (
                  <ContactItem href={`tel:${referee.contactInfo.phone}`}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                    {referee.contactInfo.phone}
                  </ContactItem>
                )}
                {referee.contactInfo?.telegram && (
                  <ContactItem href={`https://t.me/${referee.contactInfo.telegram.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.25-5.54 3.69-.52.36-1 .53-1.42.52-.47-.01-1.37-.26-2.03-.48-.82-.27-1.47-.42-1.42-.88.03-.25.35-.5.95-.75 3.74-1.63 6.24-2.7 7.5-3.23 3.57-1.49 4.31-1.75 4.79-1.75.11 0 .35.02.5.14.13.1.16.24.18.27-.01.06.01.24 0 .38z"/>
                    </svg>
                    {referee.contactInfo.telegram}
                  </ContactItem>
                )}
                {referee.contactInfo?.instagram && (
                  <ContactItem href={`https://instagram.com/${referee.contactInfo.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    @{referee.contactInfo.instagram.replace('@', '')}
                  </ContactItem>
                )}
                {referee.contactInfo?.facebook && (
                  <ContactItem href={referee.contactInfo.facebook.startsWith('http') ? referee.contactInfo.facebook : `https://facebook.com/${referee.contactInfo.facebook}`} target="_blank" rel="noopener noreferrer">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </ContactItem>
                )}
                {referee.contactInfo?.twitter && (
                  <ContactItem href={`https://twitter.com/${referee.contactInfo.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    @{referee.contactInfo.twitter.replace('@', '')}
                  </ContactItem>
                )}
                {referee.contactInfo?.website && (
                  <ContactItem href={referee.contactInfo.website.startsWith('http') ? referee.contactInfo.website : `https://${referee.contactInfo.website}`} target="_blank" rel="noopener noreferrer">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                    Website
                  </ContactItem>
                )}
              </ContactInfoSection>
            )}

            <StatsRow>
              <StatItem>
                <StatLabel>Rating</StatLabel>
                <StatValue>{referee.rating > 0 ? referee.rating.toFixed(1) : '—'}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Total Games</StatLabel>
                <StatValue>{referee.totalGames}</StatValue>
              </StatItem>
            </StatsRow>

            {/* Hide all action buttons if viewing self */}
            {!isPublicMode && referee.id !== currentRefereeId && (
              <ActionButtons>
                {/* Status actions */}
                {fieldStatus === 'pending' && (
                  <ActionButton
                    $variant="success"
                    onClick={(e) => { e.stopPropagation(); handleStatusUpdate(referee.id, 'active'); }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Activate'}
                  </ActionButton>
                )}

                {fieldStatus === 'active' && (
                  <ActionButton
                    $variant="warning"
                    onClick={(e) => { e.stopPropagation(); handleStatusUpdate(referee.id, 'pending'); }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Deactivate'}
                  </ActionButton>
                )}

                {fieldStatus !== 'blocked' && (
                  <ActionButton
                    $variant="danger"
                    onClick={(e) => { e.stopPropagation(); handleStatusUpdate(referee.id, 'blocked'); }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Block'}
                  </ActionButton>
                )}

                {fieldStatus === 'blocked' && (
                  <ActionButton
                    $variant="warning"
                    onClick={(e) => { e.stopPropagation(); handleStatusUpdate(referee.id, 'pending'); }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Unblock'}
                  </ActionButton>
                )}

                {/* Main referee action - hidden in restricted mode */}
                {!restrictedMode && onSetMainReferee && fieldStatus === 'active' && (
                  referee.isMainReferee ? (
                    <ActionButton
                      $variant="default"
                      onClick={(e) => { e.stopPropagation(); handleSetMainReferee(referee.id, false); }}
                      disabled={isMainLoading}
                    >
                      {isMainLoading ? 'Processing...' : 'Remove Main'}
                    </ActionButton>
                  ) : (
                    <ActionButton
                      $variant="primary"
                      onClick={(e) => { e.stopPropagation(); handleSetMainReferee(referee.id, true); }}
                      disabled={isMainLoading}
                    >
                      {isMainLoading ? 'Processing...' : 'Set as Main'}
                    </ActionButton>
                  )
                )}

                {/* Remove action - hidden in restricted mode */}
                {!restrictedMode && (
                  <ActionButton
                    $variant="danger"
                    onClick={(e) => { e.stopPropagation(); openRemoveConfirm(referee); }}
                    disabled={isLoading}
                  >
                    Remove
                  </ActionButton>
                )}
              </ActionButtons>
            )}
          </RefereeCard>
        );
      })}

      {/* Remove Confirmation Modal */}
      {removeConfirm.isOpen && removeConfirm.referee && (
        <ModalOverlay onClick={closeRemoveConfirm}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              Remove Referee
            </ModalTitle>
            <ModalText>
              Are you sure you want to remove <ModalRefereeName>{removeConfirm.referee.firstName} {removeConfirm.referee.lastName}</ModalRefereeName> from your field?
            </ModalText>
            <ModalText>
              This referee will no longer be able to officiate games at your field.
            </ModalText>
            <ModalButtons>
              <ModalButton $variant="cancel" onClick={closeRemoveConfirm}>
                Cancel
              </ModalButton>
              <ModalButton
                $variant="danger"
                onClick={handleRemoveReferee}
                disabled={actionLoading[removeConfirm.referee.id]}
              >
                {actionLoading[removeConfirm.referee.id] ? 'Removing...' : 'Remove'}
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default RefereeList;
